// Custom hook for dashboard data
import { useState, useEffect } from 'react';
import { AudioFile, Analytics, Activity, ChangelogItem, FileType, UserPlan } from '@types';
import { useFileStore } from '@stores/useFileStore';
import { useDashboardStore } from '@stores/useDashboardStore';
import { useAuthStore } from '@stores/useAuthStore';
import { supabase } from '@config/supabase';

export interface UseDashboardReturn {
  recentFiles: AudioFile[];
  analytics: Analytics;
  activities: Activity[];
  changelog: ChangelogItem[];
  changelogVersion: string;
  isLoading: boolean;
  error: string | null;
  refreshDashboard: () => Promise<void>;
}

export const useDashboard = (): UseDashboardReturn => {
  const { files } = useFileStore();
  const { user } = useAuthStore();
  const { 
    changelog: storeChangelog, 
    changelogVersion: storeChangelogVersion,
    isLoadingChangelog,
    loadChangelog,
    isInitialized
  } = useDashboardStore();
  const [error, setError] = useState<string | null>(null);
  const [billingPeriodStart, setBillingPeriodStart] = useState<Date | null>(null);
  const [versionsThisPeriod, setVersionsThisPeriod] = useState<number>(0);
  const [youtubeUploadsThisPeriod, setYoutubeUploadsThisPeriod] = useState<number>(0);
  const [activePublicBeatPacks, setActivePublicBeatPacks] = useState<number>(0);

  // Calculate billing period start date
  useEffect(() => {
    const calculateBillingPeriodStart = async () => {
      if (!user?.id) {
        setBillingPeriodStart(null);
        return;
      }

      try {
        const isFreePlan = user.plan === UserPlan.FREE;
        
        if (isFreePlan) {
          // Bei Free: Der erste Tag des Monats
          const now = new Date();
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          setBillingPeriodStart(startOfMonth);
        } else {
          // Bei Paid: Ab dem Zeitpunkt des Abos (subscriptionRenewsAt - billingInterval)
          // Wir holen die Subscription-Informationen aus der Datenbank
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('stripe_subscription_current_period_end, billing_interval, created_at')
            .eq('id', user.id)
            .single();

          if (profileError || !profile) {
            // Fallback: Erster Tag des Monats
            const now = new Date();
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            setBillingPeriodStart(startOfMonth);
            return;
          }

          if (profile.stripe_subscription_current_period_end) {
            // Berechne den Period-Start basierend auf dem Period-End
            const periodEnd = new Date(profile.stripe_subscription_current_period_end);
            const billingInterval = profile.billing_interval === 'yearly' ? 365 : 30;
            const periodStart = new Date(periodEnd);
            periodStart.setDate(periodStart.getDate() - billingInterval);
            setBillingPeriodStart(periodStart);
          } else {
            // Fallback: Account-Erstellungsdatum
            if (profile.created_at) {
              setBillingPeriodStart(new Date(profile.created_at));
            } else {
              // Finaler Fallback: Erster Tag des Monats
              const now = new Date();
              const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
              setBillingPeriodStart(startOfMonth);
            }
          }
        }
      } catch (error) {
        console.error('Error calculating billing period start:', error);
        // Fallback: Erster Tag des Monats
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        setBillingPeriodStart(startOfMonth);
      }
    };

    calculateBillingPeriodStart();
  }, [user?.id, user?.plan]);

  // Count versions and YouTube uploads for billing period
  useEffect(() => {
    const countBillingPeriodStats = async () => {
      if (!user?.id || !billingPeriodStart) {
        setVersionsThisPeriod(0);
        setYoutubeUploadsThisPeriod(0);
        return;
      }

      try {
        // Count versions (files with version_type set) created in billing period
        // Only use .not('version_type','is',null) – .neq('version_type','') can cause 400 (PostgREST parsing)
        const { count: versionsCount, error: versionsError } = await supabase
          .from('files')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .not('version_type', 'is', null)
          .gte('created_at', billingPeriodStart.toISOString());

        if (versionsError) {
          console.error('Error counting versions:', versionsError);
          setVersionsThisPeriod(0);
        } else {
          setVersionsThisPeriod(versionsCount || 0);
        }

        // Count YouTube uploads created in billing period
        const { count: youtubeCount, error: youtubeError } = await supabase
          .from('youtube_uploads')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .gte('created_at', billingPeriodStart.toISOString());

        if (youtubeError) {
          console.error('Error counting YouTube uploads:', youtubeError);
          setYoutubeUploadsThisPeriod(0);
        } else {
          setYoutubeUploadsThisPeriod(youtubeCount || 0);
        }
      } catch (error) {
        console.error('Error counting billing period stats:', error);
        setVersionsThisPeriod(0);
        setYoutubeUploadsThisPeriod(0);
      }
    };

    countBillingPeriodStats();
  }, [user?.id, billingPeriodStart]);

  // Count active public beat packs
  useEffect(() => {
    const countActivePublicBeatPacks = async () => {
      if (!user?.id) {
        setActivePublicBeatPacks(0);
        return;
      }

      try {
        // Count active public beat packs (is_public = true and not expired if link_expires_at exists)
        const { count, error } = await supabase
          .from('beat_packs')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('is_public', true)
          .or('link_expires_at.is.null,link_expires_at.gt.' + new Date().toISOString());

        if (error) {
          console.error('Error counting active public beat packs:', error);
          setActivePublicBeatPacks(0);
        } else {
          setActivePublicBeatPacks(count || 0);
        }
      } catch (error) {
        console.error('Error counting active public beat packs:', error);
        setActivePublicBeatPacks(0);
      }
    };

    countActivePublicBeatPacks();
  }, [user?.id]);

  // Calculate analytics from files
  const now = new Date();
  // Use UTC to avoid timezone issues - set to start of current month in UTC
  const startOfMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  
  // Files uploaded this month (compare in UTC to avoid timezone issues)
  const filesThisMonth = files.filter(f => {
    if (!f.uploadedAt) return false;
    try {
      const uploadDate = new Date(f.uploadedAt);
      // Check if date is valid
      if (isNaN(uploadDate.getTime())) return false;
      // Compare in UTC to ensure consistency
      const uploadUTC = new Date(Date.UTC(
        uploadDate.getUTCFullYear(),
        uploadDate.getUTCMonth(),
        uploadDate.getUTCDate()
      ));
      const startUTC = new Date(Date.UTC(
        startOfMonth.getUTCFullYear(),
        startOfMonth.getUTCMonth(),
        startOfMonth.getUTCDate()
      ));
      return uploadUTC >= startUTC;
    } catch (e) {
      console.warn('Invalid uploadedAt date:', f.uploadedAt, e);
      return false;
    }
  }).length;

  // Last upload date - find the most recent valid upload date
  const lastUploadDate = (() => {
    const validUploadDates = files
      .filter(f => f.uploadedAt)
      .map(f => {
        try {
          const date = new Date(f.uploadedAt!);
          return isNaN(date.getTime()) ? null : date;
        } catch (e) {
          console.warn('Invalid uploadedAt date:', f.uploadedAt, e);
          return null;
        }
      })
      .filter((date): date is Date => date !== null);
    
    if (validUploadDates.length === 0) return null;
    
    return validUploadDates.sort((a, b) => b.getTime() - a.getTime())[0];
  })();

  // Calculate file type counts - use FileType enum
  const totalBeats = files.filter(f => f.type === FileType.BEAT).length;
  const totalSamples = files.filter(f => f.type === FileType.SAMPLE).length;
  const totalLoops = files.filter(f => f.type === FileType.LOOP).length;
  
  // Calculate storage - only sum valid byte values
  const storageUsed = files.reduce((acc, f) => {
    const size = typeof f.size === 'number' && Number.isFinite(f.size) && f.size > 0 ? f.size : 0;
    return acc + size;
  }, 0);
  
  // Calculate plays and downloads - handle undefined/null values
  const totalPlays = files.reduce((acc, f) => {
    const plays = typeof f.plays === 'number' && Number.isFinite(f.plays) ? f.plays : 0;
    return acc + plays;
  }, 0);
  
  const totalDownloads = files.reduce((acc, f) => {
    const downloads = typeof f.downloads === 'number' && Number.isFinite(f.downloads) ? f.downloads : 0;
    return acc + downloads;
  }, 0);
  
  // Get top files by plays - ensure valid sorting
  const topFiles = [...files]
    .sort((a, b) => {
      const playsA = typeof a.plays === 'number' && Number.isFinite(a.plays) ? a.plays : 0;
      const playsB = typeof b.plays === 'number' && Number.isFinite(b.plays) ? b.plays : 0;
      return playsB - playsA;
    })
    .slice(0, 5);

  const analytics: Analytics = {
    totalFiles: files.length,
    totalBeats,
    totalSamples,
    totalLoops,
    storageUsed,
    totalPlays,
    totalDownloads,
    topFiles,
    recentActivity: [],
    filesThisMonth, // ECHTE Daten!
    lastUploadDate, // ECHTE Daten!
    versionsThisPeriod, // Anzahl Versionen im aktuellen Billing-Zeitraum
    youtubeUploadsThisPeriod, // Anzahl YouTube-Uploads im aktuellen Billing-Zeitraum
    activePublicBeatPacks, // Anzahl aktiver öffentlicher Beat Packs
  };

  // Get recent files (last 5) - only files with valid uploadedAt dates
  const recentFiles = files
    .filter(f => {
      if (!f.uploadedAt) return false;
      try {
        const date = new Date(f.uploadedAt);
        return !isNaN(date.getTime());
      } catch (e) {
        return false;
      }
    })
    .sort((a, b) => {
      try {
        const dateA = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
        const dateB = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
        // Handle invalid dates
        if (isNaN(dateA) && isNaN(dateB)) return 0;
        if (isNaN(dateA)) return 1; // Invalid dates go to end
        if (isNaN(dateB)) return -1;
        return dateB - dateA; // Most recent first
      } catch (e) {
        return 0;
      }
    })
    .slice(0, 5);

  // Generate real activities from files
  const activities: Activity[] = files
    .reduce<Activity[]>((acc, file) => {
      // Check if file was recently uploaded (within last 7 days)
      const uploadedAt = file.uploadedAt ? new Date(file.uploadedAt) : null;
      const updatedAt = file.updatedAt ? new Date(file.updatedAt) : null;
      
      if (!uploadedAt || isNaN(uploadedAt.getTime())) return acc;
      
      // Determine activity type based on timestamps
      const now = new Date();
      const uploadAge = now.getTime() - uploadedAt.getTime();
      const updateAge = updatedAt && !isNaN(updatedAt.getTime()) ? now.getTime() - updatedAt.getTime() : Infinity;
      
      // If updated recently and update is different from upload, it's an update
      const isRecentUpdate = updateAge < uploadAge && updateAge < 7 * 24 * 60 * 60 * 1000; // 7 days
      const isRecentUpload = uploadAge < 7 * 24 * 60 * 60 * 1000; // 7 days
      
      if (isRecentUpdate && updatedAt) {
        acc.push({
          id: `update-${file.id}`,
          type: 'tag',
          fileId: file.id,
          fileName: file.name,
          description: `updated ${file.name}`,
          timestamp: updatedAt,
        });
      } else if (isRecentUpload) {
        acc.push({
          id: `upload-${file.id}`,
          type: 'upload',
          fileId: file.id,
          fileName: file.name,
          description: `uploaded ${file.name}`,
          timestamp: uploadedAt,
        });
      }
      
      return acc;
    }, [])
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
    .slice(0, 10); // Get top 10 most recent activities

  useEffect(() => {
    // Initial load only if not already initialized
    if (!isInitialized) {
      loadChangelog();
    }
    
    // Set up Realtime subscriptions for live updates
    console.log('📡 Setting up Supabase Realtime for changelog...');
    
    // Subscribe to dashboard_content changes (INSERT, UPDATE, DELETE)
    // Listen to ALL changes, not just visible ones, so we catch visibility/archive changes too
    const dashboardContentChannel = supabase
      .channel('dashboard_changelog_updates', {
        config: {
          broadcast: { self: true },
        },
      })
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'dashboard_content',
          // No filter - listen to all changes, filtering happens in loadChangelog()
        },
        (payload) => {
          console.log('📨 Changelog content changed via Realtime:', payload.eventType, payload.new);
          // Force reload changelog when content changes
          loadChangelog(true);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to dashboard_content changes');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Error subscribing to dashboard_content changes');
        }
      });

    // Subscribe to changelog_settings changes (version updates)
    const changelogSettingsChannel = supabase
      .channel('changelog_version_updates', {
        config: {
          broadcast: { self: true },
        },
      })
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'changelog_settings',
          filter: 'id=eq.current',
        },
        (payload) => {
          console.log('📨 Changelog version changed via Realtime:', payload.new);
          const newVersion = (payload.new as any)?.version;
          if (newVersion) {
            // Update version in store by reloading changelog with new version
            loadChangelog(true);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to changelog_settings changes');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Error subscribing to changelog_settings changes');
        }
      });

    // Cleanup: remove subscriptions when component unmounts
    return () => {
      console.log('🧹 Cleaning up Realtime subscriptions for changelog');
      supabase.removeChannel(dashboardContentChannel);
      supabase.removeChannel(changelogSettingsChannel);
    };
  }, [isInitialized, loadChangelog]);

  const refreshDashboard = async () => {
    setError(null);
    
    try {
      // In production, fetch from API:
      // const data = await apiService.get('/dashboard');
      // setAnalytics(data.analytics);
      // setRecentFiles(data.recentFiles);
      // etc.
      
      // Force reload changelog if refresh is explicitly called
      const { loadChangelog: loadChangelogFromStore } = useDashboardStore.getState();
      await loadChangelogFromStore(true);
    } catch (err) {
      setError('Failed to refresh dashboard');
    }
  };

  // No auto-refresh on mount - data is calculated from files store and changelog is cached

  // Only show loading if changelog is being loaded (and not already initialized)
  // Wenn wir bereits Dateien haben (z. B. aus Hydration), sofort Inhalt anzeigen – kein Skeleton
  const shouldShowLoading = isLoadingChangelog && !isInitialized && files.length === 0;

  return {
    recentFiles,
    analytics,
    activities,
    changelog: storeChangelog,
    changelogVersion: storeChangelogVersion,
    isLoading: shouldShowLoading,
    error,
    refreshDashboard,
  };
};

